import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectOption as coreSelectOption,
  selectMultipleOptions,
} from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Actions';
import {
  getItemAttributeByIdentifier,
  getItemAttributeValueByIdentifier,
} from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/AttributeSelectors';
import {
  setBlockedMaterialId,
  setEditorItemColorAndSync,
  setEditorItemOption,
  switchAllEditorItemsFromMattToGlossAndSync,
} from '../../Reducers/Configurator/Actions';
import {
  getBlockedMaterialId,
  getEditorObjects,
  getEditorSelectedId,
  getOptionClassificationById,
  getOptionClassificationTitle,
  getOptionsForClassification,
  getSelectedEditorItem,
  getSelectedOptionForClassification,
  OptionClassificationIdentifiers,
} from '../../Reducers/Configurator/Selectors';
import { AttributeIdentifiers, BackgroundTypes, SurfaceOptionIdentifiers } from '../../Enums';
import { formatHexColor } from '../../utils/editorUnits';
import { resolveOptionDetailImageUrl, resolveOptionThumbnailUrl } from '../../utils/backgroundImage';
import { resolveItemColorOptionIds } from '../../utils/colorOptionResolve';
import { filterColorOptions, getOptionAttrValues } from '../../utils/colorOptionFilter';
import { attrIsOn } from '../../utils/optionAttributes';
import type { EditorItem } from '../../Reducers/Configurator/types';
import OptionTiles from './OptionTiles';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';

function listToString(items: string[], and: string): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(', ')} ${and} ${items[items.length - 1]}`;
}

const attrValues = getOptionAttrValues;

type ComponentOptionsSectionProps = {
  classificationId: string;
  showColorSwatch?: boolean;
  disabled?: boolean;
};

export default function ComponentOptionsSection({
  classificationId,
  showColorSwatch = false,
  disabled = false,
}: ComponentOptionsSectionProps) {
  const dispatch = useDispatch();
  const options = useSelector(getOptionsForClassification(classificationId));
  const selected = useSelector(getSelectedOptionForClassification(classificationId));
  const title = useSelector(getOptionClassificationTitle(classificationId));
  const selectedId = useSelector(getEditorSelectedId);
  const optionClassification = useSelector(getOptionClassificationById(classificationId));
  const requestInFlight = React.useRef(false);

  const selectedEditorItem = useSelector(getSelectedEditorItem);
  const editorObjects = useSelector(getEditorObjects);

  const isColor = classificationId === OptionClassificationIdentifiers.color;
  const isItemMultiselect =
    classificationId === OptionClassificationIdentifiers.color ||
    classificationId === OptionClassificationIdentifiers.surface;
  const isDisabled = disabled || (isItemMultiselect && !selectedId);
  const colorOptions = useSelector(getOptionsForClassification(OptionClassificationIdentifiers.color));

  const resolveItemOptionIdentifier = (item: EditorItem) => {
    if (isColor) {
      const ids = resolveItemColorOptionIds(item, colorOptions);
      return ids.length === 1 ? ids[0] : ids.length > 1 ? ids : null;
    }
    return item.selectedOptions?.[classificationId] ?? null;
  };

  const selectedIdentifier: string | string[] | null = (() => {
    if (selectedEditorItem) {
      return resolveItemOptionIdentifier(selectedEditorItem) ?? selected?.identifier ?? null;
    }
    if (isItemMultiselect) {
      const used = [
        ...new Set(
          Object.values(editorObjects).flatMap((item) => {
            if (isColor) return resolveItemColorOptionIds(item, colorOptions);
            const optionId = item.selectedOptions?.[classificationId] ?? null;
            return optionId ? [optionId] : [];
          })
        ),
      ];
      return used.length > 0 ? used : null;
    }
    // Global classifications (material, background) aren't tied to an editor item —
    // always reflect their current global selection so the active tile stays highlighted.
    return selected?.identifier ?? null;
  })();

  // Background: large cards with real image previews.
  // Surface (matt/gloss): a binary named choice → segmented control.
  // Material & everything else: compact tiles.
  const showImageCard = classificationId === OptionClassificationIdentifiers.background;
  const showSegmented = classificationId === OptionClassificationIdentifiers.surface;

  const materialOptions = useSelector(getOptionsForClassification(OptionClassificationIdentifiers.material));
  const materialSelected = useSelector(
    getSelectedOptionForClassification(OptionClassificationIdentifiers.material)
  );
  const selectedThickness = React.useMemo(() => {
    const id = materialSelected?.identifier;
    if (!id) return null;
    const opt = materialOptions.find((o) => o.identifier === id);
    return opt ? (attrValues(opt, AttributeIdentifiers.thickness)[0] ?? null) : null;
  }, [materialSelected?.identifier, materialOptions]);

  const materialIsGlossOnly = React.useMemo(() => {
    const id = materialSelected?.identifier;
    if (!id) return false;
    const opt = materialOptions.find((o) => o.identifier === id);
    return opt ? attrIsOn(opt, 'glossOnly') : false;
  }, [materialSelected?.identifier, materialOptions]);

  const itemSurface = selectedEditorItem?.selectedOptions?.[OptionClassificationIdentifiers.surface];
  const filteredOptions = React.useMemo(() => {
    if (!isColor) return options;
    return filterColorOptions(options, { surface: itemSurface ?? null, thickness: selectedThickness });
  }, [isColor, itemSurface, selectedThickness, options]);

  // In the surface section: if the focused element's color is finish-restricted,
  // disable the unsupported segment and explain why with a note.
  const isSurface = classificationId === OptionClassificationIdentifiers.surface;
  const { surfaceDisabledIds, surfaceNote } = React.useMemo(() => {
    if (!isSurface || !selectedEditorItem) {
      return { surfaceDisabledIds: [] as string[], surfaceNote: null as string | null };
    }

    if (materialIsGlossOnly) {
      return {
        surfaceDisabledIds: options
          .filter((o) => o.identifier === SurfaceOptionIdentifiers.matte)
          .map((o) => o.identifier),
        surfaceNote: t('surfaceSection.thicknessGlossOnlyNote'),
      };
    }

    const colorIds = resolveItemColorOptionIds(selectedEditorItem, colorOptions);
    const restrictiveOptions = colorIds
      .map((colorId) => colorOptions.find((o) => o.identifier === colorId))
      .filter(Boolean);

    const and = t('common.and');
    const colorNames = (attr: string): string[] =>
      restrictiveOptions
        .filter((o) => attrIsOn(o, attr))
        .map((o) => (o as { title?: string }).title)
        .filter(Boolean) as string[];

    const matteNames = colorNames('matteOnly');
    const glossNames = colorNames('glossOnly');

    // Impossible combination: some areas require matt, others gloss — disable everything.
    if (matteNames.length > 0 && glossNames.length > 0) {
      return {
        surfaceDisabledIds: options.map((o) => o.identifier),
        surfaceNote: t('surfaceSection.conflictingFinishNote', {
          matteColors: listToString(matteNames, and, true),
          glossColors: listToString(glossNames, and, true),
        }),
      };
    }

    if (matteNames.length > 0) {
      const colors = listToString(matteNames, and, true);
      return {
        surfaceDisabledIds: options
          .filter((o) => o.identifier === SurfaceOptionIdentifiers.gloss)
          .map((o) => o.identifier),
        surfaceNote: colors
          ? t(matteNames.length === 1
              ? 'surfaceSection.matteOnlyNoteWithColorsSingular'
              : 'surfaceSection.matteOnlyNoteWithColorsPlural',
              { colors })
          : t('surfaceSection.matteOnlyNote'),
      };
    }

    if (glossNames.length > 0) {
      const colors = listToString(glossNames, and, true);
      return {
        surfaceDisabledIds: options
          .filter((o) => o.identifier !== SurfaceOptionIdentifiers.gloss)
          .map((o) => o.identifier),
        surfaceNote: colors
          ? t(glossNames.length === 1
              ? 'surfaceSection.glossOnlyNoteWithColorsSingular'
              : 'surfaceSection.glossOnlyNoteWithColorsPlural',
              { colors })
          : t('surfaceSection.glossOnlyNote'),
      };
    }

    return { surfaceDisabledIds: [] as string[], surfaceNote: null as string | null };
  }, [isSurface, selectedEditorItem, colorOptions, options, materialIsGlossOnly]);

  const isMaterial = classificationId === OptionClassificationIdentifiers.material;
  const { materialDisabledIds, materialNoteText } = React.useMemo(() => {
    if (!isMaterial) return { materialDisabledIds: [] as string[], materialNoteText: null as string | null };
    const usedColorIds = new Set(
      Object.values(editorObjects).flatMap((item) => resolveItemColorOptionIds(item, colorOptions))
    );
    if (usedColorIds.size === 0) return { materialDisabledIds: [] as string[], materialNoteText: null };
    const usedColors = colorOptions.filter((o) => usedColorIds.has(o.identifier));
    const disabled = options
      .filter((opt) => {
        const thickness = attrValues(opt, AttributeIdentifiers.thickness)[0];
        return (
          Boolean(thickness) &&
          usedColors.some((c) => !attrValues(c, AttributeIdentifiers.thickness).includes(thickness))
        );
      })
      .map((o) => o.identifier);
    const blockingColorNames = usedColors
      .filter((c) => attrValues(c, AttributeIdentifiers.thickness).length > 0)
      .map((c) => (c as { title?: string }).title)
      .filter(Boolean) as string[];
    const disabledMaterialNames = options
      .filter((o) => disabled.includes(o.identifier))
      .map((o) => (o as { title?: string }).title)
      .filter(Boolean) as string[];

    const and = t('common.and');
    const colors = listToString(blockingColorNames, and);
    const materials = listToString(disabledMaterialNames, and);
    const noteKey =
      blockingColorNames.length === 1
        ? 'materialSection.thicknessRestrictedNoteWithColorsSingular'
        : 'materialSection.thicknessRestrictedNoteWithColorsPlural';

    return {
      materialDisabledIds: disabled,
      materialNoteText: disabled.length
        ? `${
            colors && materials
              ? t(noteKey, { colors, materials })
              : t('materialSection.thicknessRestrictedNote')
          } ${t('materialSection.changeColorHint')}`
        : null,
    };
  }, [isMaterial, editorObjects, colorOptions, options]);

  // The material note only appears after the user tries to pick a blocked thickness.
  // It hides via its OK button, on a successful material change, or automatically
  // once the blocking colors are changed so the attempted thickness is compatible again.
  // Held in the store so the per-item conflict badges hide together with the note.
  const blockedMaterialId = useSelector(getBlockedMaterialId);
  React.useEffect(() => {
    if (isMaterial && blockedMaterialId && !materialDisabledIds.includes(blockedMaterialId)) {
      dispatch(setBlockedMaterialId(null));
    }
  }, [isMaterial, blockedMaterialId, materialDisabledIds, dispatch]);
  const materialNote = blockedMaterialId ? materialNoteText : null;

  const disabledOptionIds = isSurface ? surfaceDisabledIds : isMaterial ? materialDisabledIds : [];
  const note = isSurface ? surfaceNote : isMaterial ? materialNote : null;
  const noteVariant = note ? 'warning' : 'info';

  const handleSelect = (option) => {
    if (!optionClassification) return;

    if (isMaterial) {
      // Ignore clicks while a material switch request is in flight
      if (requestInFlight.current) return;
      if (materialDisabledIds.includes(option.identifier)) {
        dispatch(setBlockedMaterialId(option.identifier));
        return;
      }
      if (blockedMaterialId) dispatch(setBlockedMaterialId(null));
    }

    if (selectedId) {
      if (isColor) {
        const hex = formatHexColor(
          getItemAttributeValueByIdentifier(option, AttributeIdentifiers.hex, '000000')
        );
        dispatch(setEditorItemColorAndSync(selectedId, hex, option.identifier));
        return;
      }

      dispatch(setEditorItemOption(selectedId, classificationId, option.identifier));

      if (isItemMultiselect) {
        const allIdentifiers = [
          ...new Set(
            Object.values(editorObjects).flatMap((item) => {
              if (item.id === selectedId) return [option.identifier];
              if (isColor) return resolveItemColorOptionIds(item, colorOptions);
              const optionId = item.selectedOptions?.[classificationId] ?? null;
              return optionId ? [optionId] : [];
            })
          ),
        ];
        dispatch(
          selectMultipleOptions([
            {
              identifier: classificationId,
              selectedoptions: allIdentifiers.map((id) => ({ identifier: id, amount: 1 })),
            },
          ])
        );
        return;
      }
    }

    const result = dispatch(coreSelectOption({ ...option, amount: 1 }, true, false, optionClassification));
    if (isMaterial) {
      requestInFlight.current = true;
      Promise.resolve(result as unknown as Promise<unknown>).finally(() => {
        requestInFlight.current = false;
        if (attrIsOn(option, 'glossOnly')) {
          dispatch(switchAllEditorItemsFromMattToGlossAndSync());
        }
      });
    }
  };

  if (!options.length && !selected?.identifier) return null;

  // Backgrounds are grouped into "Wände" (flat wall textures) and "Räume" (room scenes)
  // by each option's `background_type` attribute. Falls back to a single group when the
  // attribute isn't present (e.g. before a full import seeds it).
  if (showImageCard) {
    const imageProps = {
      selectedIdentifier,
      onSelect: handleSelect,
      disabled: isDisabled,
      showImageCard,
      getThumbnailUrl: resolveOptionThumbnailUrl,
      getPreloadUrl: resolveOptionDetailImageUrl,
    };
    const typeOf = (option: unknown) =>
      String(getItemAttributeValueByIdentifier(option, AttributeIdentifiers.backgroundType, '') || '').toLowerCase();
    const walls = filteredOptions.filter((o) => typeOf(o) === BackgroundTypes.wall);
    const rooms = filteredOptions.filter((o) => typeOf(o) === BackgroundTypes.room);
    const other = filteredOptions.filter((o) => typeOf(o) !== BackgroundTypes.wall && typeOf(o) !== BackgroundTypes.room);

    if (!walls.length && !rooms.length) {
      return <OptionTiles title={title} options={filteredOptions} {...imageProps} />;
    }
    return (
      <>
        {walls.length ? <OptionTiles title={t('backgroundPanel.groupWalls')} options={walls} {...imageProps} /> : null}
        {rooms.length ? <OptionTiles title={t('backgroundPanel.groupRooms')} options={rooms} {...imageProps} /> : null}
        {other.length ? <OptionTiles title={title} options={other} {...imageProps} /> : null}
      </>
    );
  }

  return (
    <OptionTiles
      title={title}
      options={filteredOptions}
      selectedIdentifier={selectedIdentifier}
      onSelect={handleSelect}
      disabled={isDisabled}
      showColorSwatch={showColorSwatch}
      showImageCard={showImageCard}
      segmented={showSegmented}
      disabledOptionIds={disabledOptionIds}
      onDisabledOptionSelect={isMaterial ? handleSelect : undefined}
      note={note}
      noteVariant={noteVariant}
      noteActionLabel={isMaterial && note ? t('common.ok') : undefined}
      onNoteAction={isMaterial && note ? () => dispatch(setBlockedMaterialId(null)) : undefined}
    />
  );
}
